import React, { useRef } from 'react';
import type { BillModeSelectorProps } from './types';
import { BILL_MODE_META, BILL_MODE_ORDER } from './types';

// ─── BillModeSelector ─────────────────────────────────────────────────────────

export function BillModeSelector({
  value,
  onChange,
  disabled = false,
  permissions,
  lockedModes,
  className,
}: BillModeSelectorProps) {
  const tabListRef = useRef<HTMLDivElement>(null);

  // Pills + labels derived from BILL_MODE_META (the single source of truth
  // shared with CartSidebar's bottom CTA). The visible set is filtered by
  // tenant-controlled permissions — modes declaring a `requires` key are
  // hidden when the user doesn't hold that permission.
  const modes = BILL_MODE_ORDER.map((id) => BILL_MODE_META[id]).filter((m) => {
    if (!m.requires) return true;
    return Boolean(permissions?.[m.requires]);
  });

  function handleKeyDown(e: React.KeyboardEvent<HTMLButtonElement>, index: number) {
    if (e.key === 'ArrowRight' || e.key === 'ArrowDown') {
      e.preventDefault();
      const next = (index + 1) % modes.length;
      const tabs = tabListRef.current?.querySelectorAll<HTMLButtonElement>('[role="tab"]');
      tabs?.[next]?.focus();
    } else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
      e.preventDefault();
      const prev = (index - 1 + modes.length) % modes.length;
      const tabs = tabListRef.current?.querySelectorAll<HTMLButtonElement>('[role="tab"]');
      tabs?.[prev]?.focus();
    }
  }

  return (
    <div
      ref={tabListRef}
      role="tablist"
      aria-label="Billing mode"
      className={[
        'flex rounded-lg border border-neutral-200 dark:border-neutral-600 overflow-hidden',
        disabled ? 'opacity-50 pointer-events-none' : '',
        className ?? '',
      ]
        .filter(Boolean)
        .join(' ')}
    >
      {modes.map((mode, i) => {
        const locked = lockedModes?.includes(mode.id) ?? false;
        return (
        <button
          key={mode.id}
          type="button"
          role="tab"
          aria-selected={value === mode.id}
          tabIndex={value === mode.id ? 0 : -1}
          onClick={() => !disabled && onChange(mode.id)}
          onKeyDown={(e) => handleKeyDown(e, i)}
          disabled={disabled}
          aria-label={locked ? `${mode.label} (not in your plan)` : undefined}
          className={[
            'flex-1 px-3 py-2 text-sm font-semibold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-brand-500',
            value === mode.id
              ? mode.activeClasses
              : locked
                ? 'bg-white dark:bg-neutral-800 text-neutral-400 dark:text-neutral-500 hover:bg-neutral-50 dark:hover:bg-neutral-700'
                : 'bg-white dark:bg-neutral-800 text-neutral-600 dark:text-neutral-300 hover:bg-neutral-50 dark:hover:bg-neutral-700',
          ]
            .filter(Boolean)
            .join(' ')}
        >
          {mode.label}
          {locked && (
            <span title="Not in your plan" className="ml-1 inline-flex align-[-2px] text-warning-500 dark:text-warning-400">
              <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
              </svg>
            </span>
          )}
        </button>
        );
      })}
    </div>
  );
}
